import * as React from 'react';
import { useIntl } from 'react-intl';

import { IconRegularInfo, ModalContext, Text } from '@notino/react-styleguide';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { isDesktop } from '@utils/helpers';

import { InfoModalContent } from './InfoModalContent';
import { messages } from './messages';
import {
  LineThrough,
  Percentage,
  RowWithIcon,
  StyledDivider,
  Wrapper,
} from './styled';
import { displayPercentageDiscount } from './utils';

type Props = {
  percentage: number;
  percentageFromMinimal: number | undefined;
  discountedPrice: number;
  legalText: React.ReactNode;
};

export const BottomDiscountPart = ({
  percentage,
  discountedPrice,
  percentageFromMinimal,
  legalText,
}: Props) => {
  const { formatMessage } = useIntl();
  const { currentVariant } = useProductDetailContext();
  const minPrice = currentVariant.recentMinPrice;
  const { toggleModal } = ModalContext.useModalContext();
  const { toggleDrawer } = useDrawer();

  const openInfoModal = () => {
    const props: React.ComponentProps<typeof InfoModalContent> = {
      percentage,
      percentageFromMinimal,
      discountedPrice,
      variant: currentVariant,
      legalText,
    };

    if (isDesktop()) {
      toggleModal(<InfoModalContent {...props} />);
      return;
    }
    toggleDrawer(<InfoModalContent {...props} />, {
      header: formatMessage(messages.modalTitle),
    });
  };

  const currPriceRow = (
    <Row price={currentVariant.price.value ?? 0} percentage={percentage}>
      {formatMessage(messages.currentPriceText)}
    </Row>
  );

  return (
    <Wrapper>
      <StyledDivider />

      {minPrice ? currPriceRow : null}

      <RowWithIcon>
        {minPrice ? (
          <Row
            price={minPrice.value ?? 0}
            percentage={percentageFromMinimal ?? 0}
          >
            {formatMessage(messages.recentMinPriceText)}
          </Row>
        ) : (
          currPriceRow
        )}

        <IconRegularInfo
          onClick={openInfoModal}
          width="16px"
          height="16px"
          color="icon.tertiary"
        />
      </RowWithIcon>
    </Wrapper>
  );
};

const Row = ({
  price,
  percentage,
  children,
}: React.PropsWithChildren<{ price: number; percentage: number }>) => {
  const formatPrice = useFormatPrice();

  const { formattedPriceWithCurrency: formattedPrice } = formatPrice({
    value: price,
  }) ?? { formattedPriceWithCurrency: '' };

  return (
    <Text typography="labelSmall400" color="text.secondary">
      {children} <LineThrough>{formattedPrice}</LineThrough>
      <Percentage>{displayPercentageDiscount(percentage)}</Percentage>
    </Text>
  );
};
